export type LayoutItem = {
  i: string;
  x?: number | string;
  y?: number | string;
  h: number | string;
  w: number | string;
  top_i?: string;
  left_i?: string;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
  moved?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
};
export type Layout = Array<LayoutItem>;

const getNum = (v: number | string): number => (typeof v === 'number' ? v : parseFloat(v) || 0);

const isAbsoluteValue = (v: number | string): boolean =>
  typeof v === 'string' &&
  (v.includes('px') || v.includes('calc') || v.includes('grow') || v.includes('%'));

/**
 * Return the bottom coordinate of the layout.
 *
 * @param  {Array} layout Layout array.
 * @return {Number}       Bottom coordinate.
 */
export function bottom(layout: Layout): number {
  let max = 0;
  for (let i = 0, len = layout.length; i < len; i++) {
    const l = layout[i];
    const valY = l.y ?? 0;
    const valH = l.h ?? 0;
    const bottomY = getNum(valY) + getNum(valH);
    if (bottomY > max) max = bottomY;
  }
  return max;
}

// Fast path to cloning, since this is monomorphic
export function cloneLayoutItem(layoutItem: LayoutItem): LayoutItem {
  return JSON.parse(JSON.stringify(layoutItem));
}

export function cloneLayout(layout: Layout): Layout {
  const newLayout = Array(layout.length);
  for (let i = 0, len = layout.length; i < len; i++) {
    newLayout[i] = cloneLayoutItem(layout[i]);
  }
  return newLayout;
}

/**
 * Given two layoutitems, check if they collide.
 *
 * @return {Boolean}   True if colliding.
 */
export function collides(l1: LayoutItem, l2: LayoutItem): boolean {
  if (l1 === l2) return false; // same element
  const x1 = getNum(l1.x),
    y1 = getNum(l1.y),
    w1 = getNum(l1.w),
    h1 = getNum(l1.h);
  const x2 = getNum(l2.x),
    y2 = getNum(l2.y),
    w2 = getNum(l2.w),
    h2 = getNum(l2.h);
  if (x1 + w1 <= x2) return false; // l1 is left of l2
  if (x1 >= x2 + w2) return false; // l1 is right of l2
  if (y1 + h1 <= y2) return false; // l1 is above l2
  if (y1 >= y2 + h2) return false; // l1 is below l2
  return true; // boxes overlap
}

/**
 * Given a layout, compact it. This involves going down each y coordinate and removing gaps
 * between items.
 *
 * @param  {Array} layout Layout.
 * @param  {Boolean} verticalCompact Whether or not to compact the layout
 *   vertically.
 * @return {Array}       Compacted Layout.
 */
export function compact(layout: Layout, verticalCompact: boolean): Layout {
  const compareWith = getStatics(layout);
  const sorted = sortLayoutItemsByRowCol(layout);
  const out = Array(layout.length);

  for (let i = 0, len = sorted.length; i < len; i++) {
    let l = sorted[i];

    if (!l.static) {
      l = compactItem(compareWith, l, verticalCompact);
      compareWith.push(l);
    }

    out[layout.indexOf(l)] = l;
    l.moved = false;
  }

  return out;
}

/**
 * Compact an item in the layout.
 */
export function compactItem(
  compareWith: Layout,
  l: LayoutItem,
  verticalCompact: boolean,
): LayoutItem {
  if (verticalCompact && !isAbsoluteValue(l.y) && l.top_i === undefined && l.left_i === undefined) {
    while (getNum(l.y) > 0 && !getFirstCollision(compareWith, l)) {
      l.y = getNum(l.y) - 1;
    }
  }

  let collides = {} as LayoutItem | void;
  while ((collides = getFirstCollision(compareWith, l))) {
    if (isAbsoluteValue(l.y) || l.top_i !== undefined || l.left_i !== undefined) break;
    l.y = getNum(collides.y) + getNum(collides.h);
  }
  return l;
}

/**
 * Given a layout, make sure all elements fit within its bounds.
 *
 * @param  {Array} layout Layout array.
 * @param  {Number} bounds Number of columns.
 */
export function correctBounds(layout: Layout, bounds: { cols: number }): Layout {
  const collidesWith = getStatics(layout);
  for (let i = 0, len = layout.length; i < len; i++) {
    const l = layout[i];
    const lx = getNum(l.x);
    const lw = getNum(l.w);

    if (
      lx + lw > bounds.cols &&
      !isAbsoluteValue(l.x) &&
      !isAbsoluteValue(l.w) &&
      l.left_i === undefined
    ) {
      l.x = bounds.cols - lw;
    }
    if (lx < 0 && !isAbsoluteValue(l.x) && l.left_i === undefined) {
      l.x = 0;
      l.w = bounds.cols;
    }
    if (!l.static) collidesWith.push(l);
    else {
      while (getFirstCollision(collidesWith, l)) {
        if (isAbsoluteValue(l.y) || l.top_i !== undefined || l.left_i !== undefined) break;
        l.y = getNum(l.y) + 1;
      }
    }
  }
  return layout;
}

/**
 * Get a layout item by ID. Used so we can override later on if necessary.
 *
 * @param  {Array}  layout Layout array.
 * @param  {String} id     ID
 * @return {LayoutItem}    Item at ID.
 */
export function getLayoutItem(layout: Layout, id: string): LayoutItem | undefined {
  for (let i = 0, len = layout.length; i < len; i++) {
    if (layout[i].i === id) return layout[i];
  }
}

/**
 * Returns the first item this layout collides with.
 * It doesn't appear to matter which order we approach this from, although
 * perhaps that is the wrong thing to do.
 *
 * @param  {Object} layoutItem Layout item.
 * @return {Object|undefined}  A colliding layout item, or undefined.
 */
export function getFirstCollision(layout: Layout, layoutItem: LayoutItem): LayoutItem | void {
  for (let i = 0, len = layout.length; i < len; i++) {
    if (collides(layout[i], layoutItem)) return layout[i];
  }
}

export function getAllCollisions(layout: Layout, layoutItem: LayoutItem): Layout {
  return layout.filter(l => collides(l, layoutItem));
}

/**
 * Get all static elements.
 * @param  {Array} layout Array of layout objects.
 * @return {Array}        Array of static layout items..
 */
export function getStatics(layout: Layout): Layout {
  return layout.filter(l => l.static);
}

/**
 * Move an element. Responsible for doing cascading movements of other elements.
 *
 * @param  {Array}      layout Full layout to modify.
 * @param  {LayoutItem} l      element to move.
 * @param  {Number}     [x]    X position in grid units.
 * @param  {Number}     [y]    Y position in grid units.
 * @param  {Boolean}    [isUserAction] If true, designates that the item we're moving is
 *                                     being dragged/resized by th euser.
 */
export function moveElement(
  layout: Layout,
  l: LayoutItem,
  x: number | void,
  y: number,
  isUserAction: boolean,
  preventCollision = false,
) {
  if (l.static) return layout;

  const oldX = l.x;
  const oldY = l.y;

  const movingUp = y && getNum(l.y) > y;
  if (typeof x === 'number') l.x = x;
  if (typeof y === 'number') l.y = y;
  l.moved = true;

  let sorted = sortLayoutItemsByRowCol(layout);
  if (movingUp) sorted = sorted.reverse();
  const collisions = getAllCollisions(sorted, l);

  if (preventCollision && collisions.length) {
    l.x = oldX;
    l.y = oldY;
    l.moved = false;
    return layout;
  }

  for (let i = 0, len = collisions.length; i < len; i++) {
    const collision = collisions[i];
    if (collision.moved) continue;
    if (
      getNum(l.y) > getNum(collision.y) &&
      getNum(l.y) - getNum(collision.y) > getNum(collision.h) / 4
    )
      continue;

    if (collision.static) {
      layout = moveElementAwayFromCollision(layout, collision, l, isUserAction);
    } else {
      layout = moveElementAwayFromCollision(layout, l, collision, isUserAction);
    }
  }

  return layout;
}

/**
 * This is where the magic needs to happen - given a collision, move an element away from the collision.
 * We attempt to move it up if there's room, otherwise it goes below.
 *
 * @param  {Array} layout            Full layout to modify.
 * @param  {LayoutItem} collidesWith Layout item we're colliding with.
 * @param  {LayoutItem} itemToMove   Layout item we're moving.
 * @param  {Boolean} [isUserAction]  If true, designates that the item we're moving is being dragged/resized
 *                                   by the user.
 */
export function moveElementAwayFromCollision(
  layout: Layout,
  collidesWith: LayoutItem,
  itemToMove: LayoutItem,
  isUserAction: boolean,
) {
  const preventCollision = false;
  if (isUserAction) {
    const fakeItem: LayoutItem = {
      x: itemToMove.x,
      y: itemToMove.y,
      w: itemToMove.w,
      h: itemToMove.h,
      i: '-1',
    };
    fakeItem.y = Math.max(getNum(collidesWith.y) - getNum(itemToMove.h), 0);
    if (!getFirstCollision(layout, fakeItem)) {
      return moveElement(layout, itemToMove, undefined, fakeItem.y as number, preventCollision);
    }
  }

  return moveElement(
    layout,
    itemToMove,
    undefined,
    (getNum(itemToMove.y) + 1) as number,
    preventCollision,
  );
}

export function setTransform(
  top: number | string,
  left: number | string,
  width: number | string,
  height: number | string,
) {
  const leftStr = typeof left === 'number' ? left + 'px' : left;
  const topStr = typeof top === 'number' ? (top < 0 ? 0 : top) + 'px' : top;
  const translate = 'translate3d(' + leftStr + ',' + topStr + ', 0)';
  return {
    transform: translate,
    WebkitTransform: translate,
    MozTransform: translate,
    msTransform: translate,
    OTransform: translate,
    width: typeof width === 'number' ? width + 'px' : width,
    height: typeof height === 'number' ? height + 'px' : height,
    position: 'absolute' as const,
  };
}

export function setTransformRtl(
  top: number | string,
  right: number | string,
  width: number | string,
  height: number | string,
) {
  const rightStr = typeof right === 'number' ? right * -1 + 'px' : 'calc(' + right + ' * -1)';
  const topStr = typeof top === 'number' ? (top < 0 ? 0 : top) + 'px' : top;
  const translate = 'translate3d(' + rightStr + ',' + topStr + ', 0)';
  return {
    transform: translate,
    WebkitTransform: translate,
    MozTransform: translate,
    msTransform: translate,
    OTransform: translate,
    width: typeof width === 'number' ? width + 'px' : width,
    height: typeof height === 'number' ? height + 'px' : height,
    position: 'absolute' as const,
  };
}

export function setTopLeft(
  top: number | string,
  left: number | string,
  width: number | string,
  height: number | string,
) {
  return {
    top: typeof top === 'number' ? top + 'px' : top,
    left: typeof left === 'number' ? left + 'px' : left,
    width: typeof width === 'number' ? width + 'px' : width,
    height: typeof height === 'number' ? height + 'px' : height,
    position: 'absolute' as const,
  };
}

export function setTopRight(
  top: number | string,
  right: number | string,
  width: number | string,
  height: number | string,
) {
  return {
    top: typeof top === 'number' ? top + 'px' : top,
    right: typeof right === 'number' ? right + 'px' : right,
    width: typeof width === 'number' ? width + 'px' : width,
    height: typeof height === 'number' ? height + 'px' : height,
    position: 'absolute' as const,
  };
}

export function sortLayoutItemsByRowCol(layout: Layout): Layout {
  return [...layout].sort((a, b) => {
    const ax = getNum(a.x);
    const ay = getNum(a.y);
    const bx = getNum(b.x);
    const by = getNum(b.y);

    if (ay === by && ax === bx) {
      return 0;
    }

    if (ay > by || (ay === by && ax > bx)) {
      return 1;
    }

    return -1;
  });
}

export function validateLayout(layout: Layout, contextName = 'Layout'): void {
  const subProps = ['x', 'y', 'w', 'h'];
  if (!Array.isArray(layout)) throw new Error(contextName + ' must be an array!');
  for (let i = 0, len = layout.length; i < len; i++) {
    const item = layout[i];
    for (let j = 0; j < subProps.length; j++) {
      const key = subProps[j] as keyof LayoutItem;

      if (key === 'y' && 'top_i' in item) continue;
      if (key === 'x' && 'left_i' in item) continue;
      const val = item[key];
      if (typeof val !== 'number' && typeof val !== 'string') {
        throw new Error(
          'VueGridLayout: ' +
            contextName +
            '[' +
            i +
            '].' +
            subProps[j] +
            ' must be a number or string!',
        );
      }
    }
    if (item.static !== undefined && typeof item.static !== 'boolean') {
      throw new Error('VueGridLayout: ' + contextName + '[' + i + '].static must be a boolean!');
    }
  }
}
